import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Checkbox } from '../components/ui/checkbox';
import { Plus, Search, FileText, Download, Trash2, Edit, Upload, Eye, Calendar, Clock, Users, MapPin, Wrench, X, Folder, FolderPlus, FolderOpen } from 'lucide-react';
import api from '../lib/api';
import { fmtDate } from '../lib/dateUtils';
import { useAuth } from '../contexts/AuthContext';

const WORK_ACTIVITIES = [
  'Sabbiatura', 'Pallinatura', 'Lavaggio', 'Applicazione Primer',
  'Primo Strato', 'Secondo Strato', 'Terzo Strato', 'Finitura', 'Altro'
];

const MATERIAL_TYPES = [
  { value: 'verniciante', label: 'Verniciante' },
  { value: 'abrasivo', label: 'Abrasivo' },
  { value: 'solvente', label: 'Solvente' },
  { value: 'carburante', label: 'Carburante' },
  { value: 'olio', label: 'Olio' },
  { value: 'altro', label: 'Altro' }
];

// Struttura work_item aggiornata per supportare "Altro" con descrizione
const emptyWorkItem = { activity: '', activity_description: '', sqm_done: '', sqm_remaining: '', surface: '', description: '', percentage: '' };

export default function ProductionDiaryPage() {
  const { isRole } = useAuth();
  const [diaries, setDiaries] = useState([]);
  const [sites, setSites] = useState([]);
  const [equipment, setEquipment] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [inventoryItems, setInventoryItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterSite, setFilterSite] = useState('__all__');
  
  const [showCreate, setShowCreate] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [showDetail, setShowDetail] = useState(false);
  const [selectedDiary, setSelectedDiary] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [showExportMatrice, setShowExportMatrice] = useState(false);
  const [exportDateFrom, setExportDateFrom] = useState('');
  const [exportDateTo, setExportDateTo] = useState('');
  const [exportSiteId, setExportSiteId] = useState('');

  // Folders (raggruppamento diari)
  const [folders, setFolders] = useState([]);
  const [filterFolder, setFilterFolder] = useState('__all__');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [groupedView, setGroupedView] = useState(false);
  const [showFolderManager, setShowFolderManager] = useState(false);
  const [folderForm, setFolderForm] = useState({ name: '', pdl: '', description: '', color: '#1a4b8c' });
  const [editingFolderId, setEditingFolderId] = useState(null);
  const [showMoveDialog, setShowMoveDialog] = useState(false);
  const [moveTargetFolder, setMoveTargetFolder] = useState('');
  
  const emptyForm = {
    site_id: '',
    project_id: '',
    numero_pdl: '',
    date: new Date().toISOString().split('T')[0],
    location_item: '',
    day_number: '',
    hours_worked: '',
    hours_downtime: '',
    total_sqm: '',
    remaining_sqm: '',
    personnel_count: '',
    personnel_ids: [],
    personnel_hours: [],
    inspector_name: '',
    inspector_hours: '',
    work_area_type: 'interno',
    work_items: [],
    operational_notes: '',
    quality_checks: '',
    downtime_reason: '',
    downtime_description: '',
    materials: [],
    equipment_used: [],
    compiled_by: '',
    signature_date: '',
    status: 'bozza',
    progress_today: '',
  };
  
  const [form, setForm] = useState(emptyForm);
  const [activeTab, setActiveTab] = useState('generale');

  const [equipSearch, setEquipSearch] = useState('');
  const [matSearch, setMatSearch] = useState('');
  const [projects, setProjects] = useState([]);
  const [projectProgress, setProjectProgress] = useState(0);

  const load = async () => {
    try {
      const params = {};
      if (filterSite !== '__all__') params.site_id = filterSite;
      if (filterFolder !== '__all__') params.folder_id = filterFolder;
      if (filterDateFrom) params.date_from = filterDateFrom;
      if (filterDateTo) params.date_to = filterDateTo;
      const [diariesRes, sitesRes, equipRes, empRes, invRes, projRes, foldersRes] = await Promise.all([
        api.get('/production-diary', { params }),
        api.get('/sites'),
        api.get('/equipment'),
        api.get('/employees/list/simple'),
        api.get('/inventory').catch(() => ({ data: [] })),
        api.get('/projects').catch(() => ({ data: [] })),
        api.get('/production-diary/folders/list').catch(() => ({ data: [] })),
      ]);
      setDiaries(diariesRes.data);
      setSites(sitesRes.data);
      setEquipment(equipRes.data);
      setEmployees(empRes.data);
      setInventoryItems(invRes.data);
      setProjects(projRes.data || []);
      setFolders(foldersRes.data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [filterSite, filterFolder, filterDateFrom, filterDateTo]);

  const handleCreate = async () => {
    try {
      await api.post('/production-diary', form);
      setShowCreate(false);
      setForm(emptyForm);
      load();
    } catch (err) {
      alert(err.response?.data?.detail || 'Errore');
    }
  };

  const handleUpdate = async () => {
    try {
      await api.put(`/production-diary/${selectedDiary.id}`, form);
      setShowEdit(false);
      load();
      // Refresh detail
      const res = await api.get(`/production-diary/${selectedDiary.id}`);
      setSelectedDiary(res.data);
    } catch (err) {
      alert(err.response?.data?.detail || 'Errore');
    }
  };

  const handleDelete = async (diary) => {
    if (!window.confirm(`Eliminare il diario del ${fmtDate(diary.date)}?`)) return;
    try {
      await api.delete(`/production-diary/${diary.id}`);
      setShowDetail(false);
      setSelectedDiary(null);
      load();
    } catch (err) {
      alert(err.response?.data?.detail || 'Errore');
    }
  };

  const openDetail = async (diary) => {
    const res = await api.get(`/production-diary/${diary.id}`);
    setSelectedDiary(res.data);
    setShowDetail(true);
  };

  const openEdit = () => {
    const ph = selectedDiary.personnel_hours || [];
    const pids = selectedDiary.personnel_ids || [];
    // Se personnel_hours mancano, crea voci da personnel_ids con ore di default
    const personnelHours = ph.length > 0 ? ph : pids.map(id => {
      const emp = employees.find(e => e.id === id);
      return { employee_id: id, employee_name: emp ? `${emp.first_name} ${emp.last_name}` : '', hours: 8 };
    });
    setForm({
      ...emptyForm,
      ...selectedDiary,
      personnel_ids: pids,
      personnel_hours: personnelHours,
      work_items: selectedDiary.work_items || [],
      materials: selectedDiary.materials || [],
      equipment_used: selectedDiary.equipment_used || []
    });
    setShowEdit(true);
  };

  const downloadPdf = async (diaryId) => {
    try {
      const response = await api.get(`/production-diary/${diaryId}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `diario_produzione_${selectedDiary?.date || 'export'}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      alert('Errore nel download PDF');
    }
  };

  const handleUploadAttachment = async (file) => {
    if (!selectedDiary || !file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      await api.post(`/production-diary/${selectedDiary.id}/attachments`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      // Refresh diary detail
      const res = await api.get(`/production-diary/${selectedDiary.id}`);
      setSelectedDiary(res.data);
    } catch (err) {
      alert(err.response?.data?.detail || 'Errore durante il caricamento');
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteAttachment = async (attachmentId) => {
    if (!window.confirm('Eliminare questo allegato?')) return;
    try {
      await api.delete(`/production-diary/${selectedDiary.id}/attachments/${attachmentId}`);
      // Refresh
      const res = await api.get(`/production-diary/${selectedDiary.id}`);
      setSelectedDiary(res.data);
    } catch (err) {
      alert('Errore durante l\'eliminazione');
    }
  };

  const addWorkItem = () => {
    setForm({
      ...form,
      work_items: [...form.work_items, { activity: '', activity_description: '', sqm_done: '', sqm_remaining: '', surface: '', description: '', percentage: '' }]
    });
  };

  const updateWorkItem = (index, field, value) => {
    const items = [...form.work_items];
    items[index][field] = value;
    setForm({ ...form, work_items: items });
  };

  const removeWorkItem = (index) => {
    setForm({ ...form, work_items: form.work_items.filter((_, i) => i !== index) });
  };

  const addMaterial = () => {
    setForm({
      ...form,
      materials: [...form.materials, { type: 'verniciante', name: '', ral: '', quantity: '', lot_date: '', unit: 'kg', inventory_item_id: '', auto_unload: false }]
    });
  };

  const updateMaterial = (index, field, value) => {
    const items = [...form.materials];
    items[index][field] = value;
    setForm({ ...form, materials: items });
  };

  const removeMaterial = (index) => {
    setForm({ ...form, materials: form.materials.filter((_, i) => i !== index) });
  };

  const addEquipment = () => {
    setForm({
      ...form,
      equipment_used: [...form.equipment_used, { equipment_id: '', equipment_name: '', hours_used: '', operator_id: '', operator_name: '', pdl_reference: form.numero_pdl || '', notes: '' }]
    });
  };

  const updateEquipmentUsed = (index, field, value) => {
    const items = [...form.equipment_used];
    if (field === 'equipment_id') {
      const eq = equipment.find(e => e.id === value);
      items[index]['equipment_id'] = value;
      items[index]['equipment_name'] = eq?.name || '';
    } else if (field === 'operator_id') {
      const emp = employees.find(e => e.id === value);
      items[index]['operator_id'] = value;
      items[index]['operator_name'] = emp ? `${emp.first_name} ${emp.last_name}` : '';
    } else {
      items[index][field] = value;
    }
    setForm({ ...form, equipment_used: items });
  };

  const removeEquipmentUsed = (index) => {
    setForm({ ...form, equipment_used: form.equipment_used.filter((_, i) => i !== index) });
  };

  const toggleEmployee = (empId) => {
    const current = form.personnel_ids || [];
    const currentHours = form.personnel_hours || [];
    if (current.includes(empId)) {
      setForm({
        ...form,
        personnel_ids: current.filter(id => id !== empId),
        personnel_hours: currentHours.filter(ph => ph.employee_id !== empId),
        personnel_count: current.length - 1
      });
    } else {
      const emp = employees.find(e => e.id === empId);
      setForm({
        ...form,
        personnel_ids: [...current, empId],
        personnel_hours: [...currentHours, { employee_id: empId, employee_name: emp ? `${emp.first_name} ${emp.last_name}` : '', hours: 8 }],
        personnel_count: current.length + 1
      });
    }
  };

  const updateEmployeeHours = (empId, hours) => {
    const currentHours = (form.personnel_hours || []).map(ph =>
      ph.employee_id === empId ? { ...ph, hours: parseFloat(hours) || 0 } : ph
    );
    const totalHours = currentHours.reduce((sum, ph) => sum + (ph.hours || 0), 0);
    setForm({ ...form, personnel_hours: currentHours, hours_worked: totalHours });
  };

  const getSelectedEmployeeNames = () => {
    return (form.personnel_ids || [])
      .map(id => employees.find(e => e.id === id))
      .filter(Boolean)
      .map(e => `${e.first_name} ${e.last_name}`);
  };

  // ============ FOLDERS HANDLERS ============
  const handleSaveFolder = async () => {
    const name = (folderForm.name || '').trim();
    if (!name) { alert('Nome cartella obbligatorio'); return; }
    try {
      if (editingFolderId) {
        await api.put(`/production-diary/folders/${editingFolderId}`, folderForm);
      } else {
        await api.post('/production-diary/folders', folderForm);
      }
      setFolderForm({ name: '', pdl: '', description: '', color: '#1a4b8c' });
      setEditingFolderId(null);
      const res = await api.get('/production-diary/folders/list');
      setFolders(res.data || []);
    } catch (err) { alert(err.response?.data?.detail || 'Errore'); }
  };

  const handleDeleteFolder = async (id) => {
    if (!window.confirm('Eliminare la cartella? I diari verranno scollegati ma non eliminati.')) return;
    try {
      await api.delete(`/production-diary/folders/${id}`);
      const res = await api.get('/production-diary/folders/list');
      setFolders(res.data || []);
      load();
    } catch (err) { alert(err.response?.data?.detail || 'Errore'); }
  };

  const openMoveDialog = (diary) => {
    setSelectedDiary(diary);
    setMoveTargetFolder(diary.folder_id || '__none__');
    setShowMoveDialog(true);
  };

  const handleMoveToFolder = async () => {
    if (!selectedDiary) return;
    try {
      const target = moveTargetFolder === '__none__' ? null : moveTargetFolder;
      await api.put(`/production-diary/${selectedDiary.id}/move-to-folder`, { folder_id: target });
      setShowMoveDialog(false);
      load();
    } catch (err) { alert(err.response?.data?.detail || 'Errore'); }
  };

  // Compute folder counts and grouped diaries
  const foldersMap = folders.reduce((acc, f) => { acc[f.id] = f; return acc; }, {});
  const filteredDiaries = diaries.filter(d =>
    d.site_name?.toLowerCase().includes(search.toLowerCase()) ||
    d.numero_pdl?.toLowerCase().includes(search.toLowerCase()) ||
    d.date?.includes(search)
  );
  const diariesByFolder = filteredDiaries.reduce((acc, d) => {
    const key = d.folder_id || '__none__';
    if (!acc[key]) acc[key] = [];
    acc[key].push(d);
    return acc;
  }, {});

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-pulse text-slate-400">Caricamento...</div></div>;
  }

  // Export Matrice
  const handleExportMatrice = async () => {
    if (!exportDateFrom || !exportDateTo) return alert('Seleziona le date');
    try {
      let url = `/production-diary/export-matrice/${exportDateFrom}/${exportDateTo}`;
      if (exportSiteId && exportSiteId !== 'all') url += `?site_id=${exportSiteId}`;
      const resp = await api.get(url, { responseType: 'blob' });
      const blobUrl = window.URL.createObjectURL(new Blob([resp.data]));
      const link = document.createElement('a');
      link.href = blobUrl;
      link.setAttribute('download', `Diari_Produzione_${exportDateFrom}_${exportDateTo}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      setShowExportMatrice(false);
    } catch (err) { alert('Nessun diario trovato per il periodo/cantiere selezionato'); }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="production-diary-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Diario di Produzione</h1>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => setShowFolderManager(true)} data-testid="folders-btn" className="border-indigo-300 text-indigo-700 hover:bg-indigo-50">
            <Folder size={16} className="mr-1" /> Cartelle ({folders.length})
          </Button>
          <Button variant="outline" onClick={() => setGroupedView(v => !v)} data-testid="grouped-view-btn" className={groupedView ? 'bg-indigo-50 border-indigo-300 text-indigo-700' : ''}>
            <FolderOpen size={16} className="mr-1" /> {groupedView ? 'Vista Lista' : 'Vista Raggruppata'}
          </Button>
          <Button variant="outline" onClick={() => setShowExportMatrice(true)} data-testid="export-matrice-btn" className="border-green-300 text-green-700 hover:bg-green-50">
            <Download size={16} className="mr-1" /> Esporta Matrice
          </Button>
          <Button onClick={() => { setForm(emptyForm); setActiveTab('generale'); setShowCreate(true); }} data-testid="add-diary-btn">
            <Plus size={16} className="mr-1" /> Nuovo Diario
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col lg:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
          <Input
            placeholder="Cerca per cantiere, PDL, data..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>
        <Select value={filterSite} onValueChange={setFilterSite}>
          <SelectTrigger className="lg:w-44" data-testid="filter-site"><SelectValue placeholder="Cantiere" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">Tutti i cantieri</SelectItem>
            {sites.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filterFolder} onValueChange={setFilterFolder}>
          <SelectTrigger className="lg:w-44" data-testid="filter-folder"><SelectValue placeholder="Cartella" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">Tutte le cartelle</SelectItem>
            <SelectItem value="__none__">Senza cartella</SelectItem>
            {folders.map(f => <SelectItem key={f.id} value={f.id}>{f.name}{f.pdl ? ` - PDL ${f.pdl}` : ''}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="flex gap-2 items-center">
          <Input type="date" value={filterDateFrom} onChange={e => setFilterDateFrom(e.target.value)} className="lg:w-40" placeholder="Da" data-testid="filter-date-from" />
          <span className="text-slate-400 text-sm">a</span>
          <Input type="date" value={filterDateTo} onChange={e => setFilterDateTo(e.target.value)} className="lg:w-40" placeholder="A" data-testid="filter-date-to" />
          {(filterDateFrom || filterDateTo) && (
            <Button variant="ghost" size="sm" onClick={() => { setFilterDateFrom(''); setFilterDateTo(''); }}>
              <X size={14} />
            </Button>
          )}
        </div>
      </div>

      {/* List */}
      {groupedView ? (
        <div className="space-y-4">
          {folders.length === 0 && Object.keys(diariesByFolder).length === 1 && diariesByFolder.__none__ ? (
            <Card className="border-slate-200 shadow-sm">
              <CardContent className="p-6 text-center text-slate-500">
                Nessuna cartella creata. Clicca su "Cartelle" per crearne una e raggruppare i diari.
              </CardContent>
            </Card>
          ) : null}
          {[...folders, { id: '__none__', name: 'Senza Cartella', color: '#94a3b8', pdl: '' }].map(folder => {
            const items = diariesByFolder[folder.id] || [];
            if (items.length === 0) return null;
            return (
              <Card key={folder.id} className="border-slate-200 shadow-sm overflow-hidden" data-testid={`folder-group-${folder.id}`}>
                <div className="px-4 py-3 flex items-center justify-between border-b" style={{ background: `${folder.color}15`, borderColor: `${folder.color}40` }}>
                  <div className="flex items-center gap-2">
                    <FolderOpen size={18} style={{ color: folder.color }} />
                    <span className="font-semibold text-slate-900">{folder.name}</span>
                    {folder.pdl && <Badge variant="outline" className="bg-white">PDL {folder.pdl}</Badge>}
                    <Badge variant="outline" className="bg-white">{items.length} diari</Badge>
                  </div>
                </div>
                <CardContent className="p-0 overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Data</TableHead>
                        <TableHead>Cantiere</TableHead>
                        <TableHead className="hidden sm:table-cell">N. PDL</TableHead>
                        <TableHead className="text-right hidden sm:table-cell">Ore</TableHead>
                        <TableHead className="text-right hidden md:table-cell">MQ</TableHead>
                        <TableHead>Stato</TableHead>
                        <TableHead className="w-24">Azioni</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {items.map(diary => (
                        <TableRow key={diary.id} className="hover:bg-slate-50 cursor-pointer" onClick={() => openDetail(diary)} data-testid={`diary-row-${diary.id}`}>
                          <TableCell className="font-medium">{fmtDate(diary.date)}</TableCell>
                          <TableCell>{diary.site_name || '-'}</TableCell>
                          <TableCell className="hidden sm:table-cell text-slate-600">{diary.numero_pdl ? diary.numero_pdl.split(',').map((p,i) => <span key={i} className="inline-block mr-1 px-1.5 py-0.5 bg-purple-50 text-purple-700 border border-purple-200 rounded text-xs">{p.trim()}</span>) : '-'}</TableCell>
                          <TableCell className="text-right hidden sm:table-cell">{diary.hours_worked || 0}h</TableCell>
                          <TableCell className="text-right hidden md:table-cell">{diary.total_sqm || 0}</TableCell>
                          <TableCell>
                            <Badge variant="outline" className={diary.status === 'completato' ? 'bg-green-50 text-green-700 border-green-200' : 'bg-amber-50 text-amber-700 border-amber-200'}>
                              {diary.status === 'completato' ? 'Completato' : 'Bozza'}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <div className="flex gap-1">
                              <Button variant="ghost" size="sm" className="p-1 h-auto" onClick={(e) => { e.stopPropagation(); openDetail(diary); }}><Eye size={14} /></Button>
                              <Button variant="ghost" size="sm" className="p-1 h-auto" onClick={(e) => { e.stopPropagation(); openMoveDialog(diary); }} title="Sposta in cartella"><Folder size={14} /></Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            );
          })}
        </div>
      ) : (
      <Card className="border-slate-200 shadow-sm">
        <CardContent className="p-0 overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Data</TableHead>
                <TableHead>Cantiere</TableHead>
                <TableHead className="hidden sm:table-cell">N. PDL</TableHead>
                <TableHead className="hidden md:table-cell">Cartella</TableHead>
                <TableHead className="text-right hidden sm:table-cell">Ore</TableHead>
                <TableHead className="text-right hidden md:table-cell">MQ</TableHead>
                <TableHead>Stato</TableHead>
                <TableHead className="w-24">Azioni</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredDiaries.length === 0 ? (
                <TableRow><TableCell colSpan={8} className="text-center py-8 text-slate-500">Nessun diario trovato</TableCell></TableRow>
              ) : filteredDiaries.map(diary => {
                const folder = foldersMap[diary.folder_id];
                return (
                <TableRow key={diary.id} className="hover:bg-slate-50 cursor-pointer" onClick={() => openDetail(diary)} data-testid={`diary-row-${diary.id}`}>
                  <TableCell className="font-medium">{fmtDate(diary.date)}</TableCell>
                  <TableCell>{diary.site_name || '-'}</TableCell>
                  <TableCell className="hidden sm:table-cell text-slate-600">{diary.numero_pdl ? diary.numero_pdl.split(',').map((p,i) => <span key={i} className="inline-block mr-1 px-1.5 py-0.5 bg-purple-50 text-purple-700 border border-purple-200 rounded text-xs">{p.trim()}</span>) : '-'}</TableCell>
                  <TableCell className="hidden md:table-cell">
                    {folder ? (
                      <Badge variant="outline" style={{ borderColor: `${folder.color}80`, color: folder.color, background: `${folder.color}10` }}>
                        <Folder size={11} className="mr-1" /> {folder.name}
                      </Badge>
                    ) : <span className="text-xs text-slate-400">-</span>}
                  </TableCell>
                  <TableCell className="text-right hidden sm:table-cell">{diary.hours_worked || 0}h</TableCell>
                  <TableCell className="text-right hidden md:table-cell">{diary.total_sqm || 0}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className={diary.status === 'completato' ? 'bg-green-50 text-green-700 border-green-200' : 'bg-amber-50 text-amber-700 border-amber-200'}>
                      {diary.status === 'completato' ? 'Completato' : 'Bozza'}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="sm" className="p-1 h-auto" onClick={(e) => { e.stopPropagation(); openDetail(diary); }}><Eye size={14} /></Button>
                      <Button variant="ghost" size="sm" className="p-1 h-auto" onClick={(e) => { e.stopPropagation(); openMoveDialog(diary); }} title="Sposta in cartella"><Folder size={14} /></Button>
                    </div>
                  </TableCell>
                </TableRow>
              );})}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={showCreate || showEdit} onOpenChange={(open) => { if (!open) { setShowCreate(false); setShowEdit(false); } }}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{showEdit ? 'Modifica Diario' : 'Nuovo Diario di Produzione'}</DialogTitle>
            <DialogDescription>Compila i campi del diario di produzione</DialogDescription>
          </DialogHeader>
          
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid grid-cols-5 w-full">
              <TabsTrigger value="generale">Generale</TabsTrigger>
              <TabsTrigger value="lavorazioni">Lavorazioni</TabsTrigger>
              <TabsTrigger value="materiali">Materiali</TabsTrigger>
              <TabsTrigger value="attrezzature">Attrezzature</TabsTrigger>
              <TabsTrigger value="note">Note</TabsTrigger>
            </TabsList>

            {/* Tab Generale */}
            <TabsContent value="generale" className="space-y-4 mt-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="col-span-2 space-y-1.5">
                  <Label>Cantiere *</Label>
                  <Select value={form.site_id} onValueChange={(v) => setForm({ ...form, site_id: v })}>
                    <SelectTrigger data-testid="diary-site"><SelectValue placeholder="Seleziona..." /></SelectTrigger>
                    <SelectContent>{sites.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>Progetto</Label>
                  <Select value={form.project_id || '__none__'} onValueChange={(v) => {
                    const pid = v === '__none__' ? '' : v;
                    const proj = projects.find(p => p.id === pid);
                    setProjectProgress(proj?.progress_percentage || 0);
                    setForm({ ...form, project_id: pid });
                  }}>
                    <SelectTrigger><SelectValue placeholder="Seleziona progetto..." /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">Nessun progetto</SelectItem>
                      {projects.filter(p => !form.site_id || p.site_id === form.site_id).map(p => (
                        <SelectItem key={p.id} value={p.id}>{p.name} ({p.progress_percentage || 0}%)</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {form.project_id && (
                  <div className="space-y-1.5">
                    <Label>Avanzamento Oggi (%)</Label>
                    <div className="flex items-center gap-2">
                      <Input type="number" step="0.5" min="0" max={100 - projectProgress} value={form.progress_today} onChange={(e) => setForm({ ...form, progress_today: e.target.value })} placeholder="0" className="h-8" />
                      <span className="text-xs text-slate-500 whitespace-nowrap">Da {projectProgress}% a {Math.min(100, projectProgress + (parseFloat(form.progress_today) || 0))}%</span>
                    </div>
                  </div>
                )}
                <div className="space-y-1.5">
                  <Label>N. PDL</Label>
                  <Input value={form.numero_pdl} onChange={(e) => setForm({ ...form, numero_pdl: e.target.value })} placeholder="Es: PDL-001, PDL-002" />
                  <p className="text-xs text-slate-400">Separa piu' PDL con la virgola</p>
                </div>
                <div className="space-y-1.5">
                  <Label>Data *</Label>
                  <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="diary-date" />
                </div>
                <div className="space-y-1.5">
                  <Label>Luogo / Item</Label>
                  <Input value={form.location_item} onChange={(e) => setForm({ ...form, location_item: e.target.value })} />
                </div>
                <div className="space-y-1.5">
                  <Label>Giorno N.</Label>
                  <Input value={form.day_number} onChange={(e) => setForm({ ...form, day_number: e.target.value })} />
                </div>
                <div className="space-y-1.5">
                  <Label>Ore Lavorate</Label>
                  <Input type="number" step="0.5" value={form.hours_worked} onChange={(e) => setForm({ ...form, hours_worked: e.target.value })} />
                </div>
                <div className="space-y-1.5">
                  <Label>Ore Fermo</Label>
                  <Input type="number" step="0.5" value={form.hours_downtime} onChange={(e) => setForm({ ...form, hours_downtime: e.target.value })} />
                </div>
                <div className="space-y-1.5">
                  <Label>MQ Totali</Label>
                  <Input type="number" step="0.01" value={form.total_sqm} onChange={(e) => setForm({ ...form, total_sqm: e.target.value })} />
                </div>
                <div className="space-y-1.5">
                  <Label>MQ Rimanenti</Label>
                  <Input type="number" step="0.01" value={form.remaining_sqm} onChange={(e) => setForm({ ...form, remaining_sqm: e.target.value })} />
                </div>
                <div className="col-span-2 md:col-span-4 space-y-1.5">
                  <Label>Personale Impiegato ({(form.personnel_ids || []).length} selezionati)</Label>
                  <div className="border rounded-md p-3 max-h-40 overflow-y-auto bg-slate-50">
                    {employees.length === 0 ? (
                      <p className="text-sm text-slate-400">Nessun dipendente disponibile</p>
                    ) : (
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                        {employees.map(emp => (
                          <label key={emp.id} className="flex items-center gap-2 cursor-pointer hover:bg-slate-100 p-1 rounded text-sm">
                            <Checkbox
                              checked={(form.personnel_ids || []).includes(emp.id)}
                              onCheckedChange={() => toggleEmployee(emp.id)}
                            />
                            <span>{emp.first_name} {emp.last_name}</span>
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                  {(form.personnel_ids || []).length > 0 && (
                    <div className="mt-2 space-y-1">
                      <Label className="text-xs text-slate-600">Ore per Dipendente</Label>
                      <div className="border rounded-md divide-y bg-white">
                        {(form.personnel_hours || []).map((ph) => {
                          const emp = employees.find(e => e.id === ph.employee_id);
                          return (
                            <div key={ph.employee_id} className="flex items-center justify-between px-3 py-1.5">
                              <div className="flex items-center gap-2">
                                <button type="button" onClick={() => toggleEmployee(ph.employee_id)} className="text-red-400 hover:text-red-600"><X size={14} /></button>
                                <span className="text-sm">{emp ? `${emp.first_name} ${emp.last_name}` : ph.employee_name}</span>
                              </div>
                              <div className="flex items-center gap-1">
                                <Input
                                  type="number" step="0.5" min="0" max="24"
                                  value={ph.hours}
                                  onChange={(e) => updateEmployeeHours(ph.employee_id, e.target.value)}
                                  className="h-7 w-16 text-center text-sm"
                                  data-testid={`emp-hours-${ph.employee_id}`}
                                />
                                <span className="text-xs text-slate-500">h</span>
                              </div>
                            </div>
                          );
                        })}
                        <div className="flex items-center justify-between px-3 py-1.5 bg-slate-50 font-medium text-sm">
                          <span>Totale ore</span>
                          <span>{(form.personnel_hours || []).reduce((s, ph) => s + (ph.hours || 0), 0)}h</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                <div className="space-y-1.5">
                  <Label>Area Lavoro</Label>
                  <Select value={form.work_area_type} onValueChange={(v) => setForm({ ...form, work_area_type: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="interno">Interno</SelectItem>
                      <SelectItem value="esterno">Esterno</SelectItem>
                      <SelectItem value="cabina">Cabina</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>Ispettore</Label>
                  <Input value={form.inspector_name} onChange={(e) => setForm({ ...form, inspector_name: e.target.value })} />
                </div>
                <div className="space-y-1.5">
                  <Label>Ore Ispettore</Label>
                  <Input type="number" step="0.5" value={form.inspector_hours} onChange={(e) => setForm({ ...form, inspector_hours: e.target.value })} />
                </div>
              </div>
            </TabsContent>

            {/* Tab Lavorazioni */}
            <TabsContent value="lavorazioni" className="space-y-4 mt-4">
              <div className="flex justify-between items-center">
                <p className="text-sm text-slate-600">Registra le lavorazioni eseguite</p>
                <Button variant="outline" size="sm" onClick={addWorkItem}><Plus size={14} className="mr-1" />Aggiungi</Button>
              </div>
              {form.work_items.length === 0 ? (
                <p className="text-center text-slate-400 py-4">Nessuna lavorazione. Clicca "Aggiungi" per iniziare.</p>
              ) : (
                <div className="space-y-3">
                  {form.work_items.map((item, idx) => (
                    <Card key={idx} className="p-3 border-slate-200">
                      <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
                        <div className="col-span-2">
                          <Label className="text-xs">Attivita</Label>
                          <Select value={item.activity} onValueChange={(v) => updateWorkItem(idx, 'activity', v)}>
                            <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Seleziona..." /></SelectTrigger>
                            <SelectContent>{WORK_ACTIVITIES.map(a => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
                          </Select>
                        </div>
                        {item.activity === 'Altro' && (
                          <div className="col-span-2">
                            <Label className="text-xs">Specifica Attivita *</Label>
                            <Input className="h-8 text-xs" value={item.activity_description || ''} onChange={(e) => updateWorkItem(idx, 'activity_description', e.target.value)} placeholder="Descrivi l'attivita..." />
                          </div>
                        )}
                        <div><Label className="text-xs">MQ Fatti</Label><Input className="h-8 text-xs" type="number" value={item.sqm_done} onChange={(e) => updateWorkItem(idx, 'sqm_done', e.target.value)} /></div>
                        <div><Label className="text-xs">MQ Rim.</Label><Input className="h-8 text-xs" type="number" value={item.sqm_remaining} onChange={(e) => updateWorkItem(idx, 'sqm_remaining', e.target.value)} /></div>
                        <div><Label className="text-xs">Superficie</Label><Input className="h-8 text-xs" value={item.surface} onChange={(e) => updateWorkItem(idx, 'surface', e.target.value)} /></div>
                        <div className="flex items-end gap-1">
                          <div className="flex-1"><Label className="text-xs">%</Label><Input className="h-8 text-xs" type="number" value={item.percentage} onChange={(e) => updateWorkItem(idx, 'percentage', e.target.value)} /></div>
                          <Button variant="ghost" size="sm" className="p-1 h-8 text-red-600" onClick={() => removeWorkItem(idx)}><Trash2 size={14} /></Button>
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>

            {/* Tab Materiali */}
            <TabsContent value="materiali" className="space-y-4 mt-4">
              <div className="flex justify-between items-center">
                <p className="text-sm text-slate-600">Materiali utilizzati (con scarico automatico magazzino opzionale)</p>
                <Button variant="outline" size="sm" onClick={addMaterial}><Plus size={14} className="mr-1" />Aggiungi</Button>
              </div>
              {form.materials.length === 0 ? (
                <p className="text-center text-slate-400 py-4">Nessun materiale registrato.</p>
              ) : (
                <div className="space-y-3">
                  {form.materials.map((mat, idx) => (
                    <Card key={idx} className="p-3 border-slate-200">
                      <div className="grid grid-cols-2 md:grid-cols-7 gap-2">
                        <div>
                          <Label className="text-xs">Tipo</Label>
                          <Select value={mat.type} onValueChange={(v) => updateMaterial(idx, 'type', v)}>
                            <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                            <SelectContent>{MATERIAL_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                          </Select>
                        </div>
                        <div className="col-span-2 relative">
                          <Label className="text-xs">Articolo Magazzino</Label>
                          <Input
                            className="h-8 text-xs"
                            value={mat.inventory_item_id ? (inventoryItems.find(i => i.id === mat.inventory_item_id)?.name || mat.name) : matSearch}
                            onChange={(e) => {
                              setMatSearch(e.target.value);
                              updateMaterial(idx, 'name', e.target.value);
                              updateMaterial(idx, 'inventory_item_id', '');
                            }}
                            placeholder="Cerca nel magazzino..."
                            onFocus={() => setMatSearch(mat.name || '')}
                          />
                          {matSearch && !mat.inventory_item_id && (
                            <div className="absolute z-50 w-full mt-1 max-h-32 overflow-y-auto bg-white border rounded-md shadow-lg">
                              {inventoryItems.filter(i => 
                                `${i.name} ${i.code || ''}`.toLowerCase().includes(matSearch.toLowerCase())
                              ).slice(0, 8).map(i => (
                                <div key={i.id} className="px-2 py-1 text-xs hover:bg-slate-100 cursor-pointer" onClick={() => {
                                  updateMaterial(idx, 'inventory_item_id', i.id);
                                  updateMaterial(idx, 'name', i.name);
                                  setMatSearch('');
                                }}>
                                  {i.name} ({i.quantity_available || 0} {i.unit_of_measure || ''})
                                </div>
                              ))}
                              {inventoryItems.filter(i => `${i.name} ${i.code || ''}`.toLowerCase().includes(matSearch.toLowerCase())).length === 0 && (
                                <div className="px-2 py-1 text-xs text-slate-400">Non trovato - inserisci manualmente</div>
                              )}
                            </div>
                          )}
                          {mat.inventory_item_id && (
                            <Button variant="ghost" size="sm" className="absolute right-0 top-5 p-1 h-8 text-slate-400" onClick={() => { updateMaterial(idx, 'inventory_item_id', ''); setMatSearch(mat.name); }}>
                              <X size={12} />
                            </Button>
                          )}
                        </div>
                        <div><Label className="text-xs">Nome/Descrizione</Label><Input className="h-8 text-xs" value={mat.name} onChange={(e) => updateMaterial(idx, 'name', e.target.value)} /></div>
                        <div><Label className="text-xs">RAL / Mesh</Label><Input className="h-8 text-xs" value={mat.ral} onChange={(e) => updateMaterial(idx, 'ral', e.target.value)} placeholder="Es. 9010" /></div>
                        <div><Label className="text-xs">Quantita</Label><Input className="h-8 text-xs" type="number" value={mat.quantity} onChange={(e) => updateMaterial(idx, 'quantity', e.target.value)} /></div>
                        <div className="flex items-end gap-1">
                          {mat.inventory_item_id && (
                            <div className="flex items-center gap-1 mr-2">
                              <Checkbox 
                                id={`auto-unload-${idx}`}
                                checked={mat.auto_unload || false} 
                                onCheckedChange={(v) => updateMaterial(idx, 'auto_unload', v)}
                                className="h-4 w-4"
                              />
                              <Label htmlFor={`auto-unload-${idx}`} className="text-[10px] text-slate-600">Scarica</Label>
                            </div>
                          )}
                          <Button variant="ghost" size="sm" className="p-1 h-8 text-red-600" onClick={() => removeMaterial(idx)}><Trash2 size={14} /></Button>
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>

            {/* Tab Attrezzature */}
            <TabsContent value="attrezzature" className="space-y-4 mt-4">
              <div className="flex justify-between items-center">
                <p className="text-sm text-slate-600">Attrezzature utilizzate (con operatore e riferimento PDL)</p>
                <Button variant="outline" size="sm" onClick={addEquipment}><Plus size={14} className="mr-1" />Aggiungi</Button>
              </div>
              {form.equipment_used.length === 0 ? (
                <p className="text-center text-slate-400 py-4">Nessuna attrezzatura registrata.</p>
              ) : (
                <div className="space-y-3">
                  {form.equipment_used.map((eq, idx) => (
                    <Card key={idx} className="p-3 border-slate-200">
                      <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
                        <div className="col-span-2 relative">
                          <Label className="text-xs">Attrezzatura</Label>
                          <Input
                            className="h-8 text-xs"
                            value={eq.equipment_name || equipSearch}
                            onChange={(e) => {
                              setEquipSearch(e.target.value);
                              updateEquipmentUsed(idx, 'equipment_name', e.target.value);
                              updateEquipmentUsed(idx, 'equipment_id', '');
                            }}
                            placeholder="Cerca attrezzatura..."
                            onFocus={() => setEquipSearch(eq.equipment_name || '')}
                          />
                          {equipSearch && !eq.equipment_id && (
                            <div className="absolute z-50 w-full mt-1 max-h-32 overflow-y-auto bg-white border rounded-md shadow-lg">
                              {equipment.filter(e => 
                                `${e.name} ${e.internal_code}`.toLowerCase().includes(equipSearch.toLowerCase())
                              ).slice(0, 8).map(e => (
                                <div key={e.id} className="px-2 py-1 text-xs hover:bg-slate-100 cursor-pointer" onClick={() => {
                                  updateEquipmentUsed(idx, 'equipment_id', e.id);
                                  setEquipSearch('');
                                }}>
                                  {e.name} ({e.internal_code})
                                </div>
                              ))}
                              {equipment.filter(e => `${e.name} ${e.internal_code}`.toLowerCase().includes(equipSearch.toLowerCase())).length === 0 && (
                                <div className="px-2 py-1 text-xs text-slate-400">Nessun risultato - verra usato come testo libero</div>
                              )}
                            </div>
                          )}
                        </div>
                        <div>
                          <Label className="text-xs">Ore Utilizzo</Label>
                          <Input className="h-8 text-xs" type="number" step="0.5" value={eq.hours_used || eq.hours || ''} onChange={(e) => updateEquipmentUsed(idx, 'hours_used', e.target.value)} />
                        </div>
                        <div>
                          <Label className="text-xs">Operatore</Label>
                          <Select value={eq.operator_id || '__none__'} onValueChange={(v) => updateEquipmentUsed(idx, 'operator_id', v === '__none__' ? '' : v)}>
                            <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Seleziona..." /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="__none__">-</SelectItem>
                              {employees.map(e => <SelectItem key={e.id} value={e.id}>{e.last_name} {e.first_name}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label className="text-xs">Rif. PDL</Label>
                          <Input className="h-8 text-xs" value={eq.pdl_reference || form.numero_pdl || ''} onChange={(e) => updateEquipmentUsed(idx, 'pdl_reference', e.target.value)} placeholder="N. PDL" />
                        </div>
                        <div className="flex items-end gap-1">
                          <div className="flex-1"><Label className="text-xs">Note</Label><Input className="h-8 text-xs" value={eq.notes} onChange={(e) => updateEquipmentUsed(idx, 'notes', e.target.value)} /></div>
                          <Button variant="ghost" size="sm" className="p-1 h-8 text-red-600" onClick={() => removeEquipmentUsed(idx)}><Trash2 size={14} /></Button>
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>

            {/* Tab Note */}
            <TabsContent value="note" className="space-y-4 mt-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label>Note Operative</Label>
                  <Textarea value={form.operational_notes} onChange={(e) => setForm({ ...form, operational_notes: e.target.value })} className="min-h-[100px]" placeholder="Note sulle attivita svolte..." />
                </div>
                <div className="space-y-1.5">
                  <Label>Controlli Effettuati</Label>
                  <Textarea value={form.quality_checks} onChange={(e) => setForm({ ...form, quality_checks: e.target.value })} className="min-h-[100px]" placeholder="Controlli qualita..." />
                </div>
              </div>
              {(parseFloat(form.hours_downtime) || 0) > 0 && (
                <Card className="p-4 border-amber-200 bg-amber-50">
                  <p className="text-sm font-medium text-amber-800 mb-2">Dettagli Ore di Fermo ({form.hours_downtime}h)</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label className="text-xs">Motivo Fermo</Label>
                      <Input value={form.downtime_reason} onChange={(e) => setForm({ ...form, downtime_reason: e.target.value })} placeholder="Es. Maltempo, guasto..." />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs">Descrizione</Label>
                      <Input value={form.downtime_description} onChange={(e) => setForm({ ...form, downtime_description: e.target.value })} />
                    </div>
                  </div>
                </Card>
              )}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-4 border-t">
                <div className="space-y-1.5">
                  <Label>Compilato da</Label>
                  <Input value={form.compiled_by} onChange={(e) => setForm({ ...form, compiled_by: e.target.value })} />
                </div>
                <div className="space-y-1.5">
                  <Label>Data Firma</Label>
                  <Input type="date" value={form.signature_date} onChange={(e) => setForm({ ...form, signature_date: e.target.value })} />
                </div>
                <div className="space-y-1.5">
                  <Label>Stato</Label>
                  <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="bozza">Bozza</SelectItem>
                      <SelectItem value="completato">Completato</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </TabsContent>
          </Tabs>

          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => { setShowCreate(false); setShowEdit(false); }}>Annulla</Button>
            <Button onClick={showEdit ? handleUpdate : handleCreate} disabled={!form.site_id || !form.date} data-testid="diary-save-btn">
              {showEdit ? 'Salva Modifiche' : 'Crea Diario'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail Dialog */}
      <Dialog open={showDetail} onOpenChange={setShowDetail}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          {selectedDiary && (
            <>
              <DialogHeader>
                <div className="flex items-start justify-between">
                  <div>
                    <DialogTitle>Diario {fmtDate(selectedDiary.date)}</DialogTitle>
                    <p className="text-sm text-slate-500">{selectedDiary.site_name} - PDL: {selectedDiary.numero_pdl ? selectedDiary.numero_pdl.split(',').map(p => p.trim()).join(', ') : 'N/A'}</p>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => downloadPdf(selectedDiary.id)}>
                      <Download size={14} className="mr-1" /> PDF
                    </Button>
                    <Button variant="outline" size="sm" onClick={openEdit}>
                      <Edit size={14} className="mr-1" /> Modifica
                    </Button>
                    <Button variant="destructive" size="sm" onClick={() => handleDelete(selectedDiary)}>
                      <Trash2 size={14} />
                    </Button>
                  </div>
                </div>
              </DialogHeader>

              <div className="space-y-4 mt-4">
                {/* General Info */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 p-4 bg-slate-50 rounded-lg">
                  <div><p className="text-xs text-slate-500">Ore Lavorate</p><p className="font-medium">{selectedDiary.hours_worked || 0}h</p></div>
                  <div><p className="text-xs text-slate-500">Ore Fermo</p><p className="font-medium">{selectedDiary.hours_downtime || 0}h</p></div>
                  <div><p className="text-xs text-slate-500">MQ Totali</p><p className="font-medium">{selectedDiary.total_sqm || 0}</p></div>
                  <div><p className="text-xs text-slate-500">MQ Rimanenti</p><p className="font-medium">{selectedDiary.remaining_sqm || 0}</p></div>
                  <div><p className="text-xs text-slate-500">N. Personale</p><p className="font-medium">{selectedDiary.personnel_count || (selectedDiary.personnel_ids?.length || 0)}</p></div>
                  <div><p className="text-xs text-slate-500">Area</p><p className="font-medium capitalize">{selectedDiary.work_area_type}</p></div>
                  <div><p className="text-xs text-slate-500">Ispettore</p><p className="font-medium">{selectedDiary.inspector_name || '-'}</p></div>
                  <div><p className="text-xs text-slate-500">Ore Ispettore</p><p className="font-medium">{selectedDiary.inspector_hours || 0}h</p></div>
                </div>

                {/* Personnel Names */}
                {selectedDiary.personnel_names?.length > 0 && (
                  <div>
                    <p className="text-sm font-medium mb-2">Personale Impiegato</p>
                    <div className="flex flex-wrap gap-2">
                      {selectedDiary.personnel_names.map((name, i) => (
                        <Badge key={i} variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                          <Users size={12} className="mr-1" />
                          {name}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                {/* Work Items */}
                {selectedDiary.work_items?.length > 0 && (
                  <div>
                    <p className="text-sm font-medium mb-2">Lavorazioni</p>
                    <Table>
                      <TableHeader><TableRow><TableHead>Attivita</TableHead><TableHead className="text-right">MQ</TableHead><TableHead className="text-right">%</TableHead></TableRow></TableHeader>
                      <TableBody>
                        {selectedDiary.work_items.map((item, i) => (
                          <TableRow key={i}>
                            <TableCell>
                              {item.activity === 'Altro' && item.activity_description 
                                ? `Altro: ${item.activity_description}` 
                                : item.activity}
                            </TableCell>
                            <TableCell className="text-right">{item.sqm_done}</TableCell>
                            <TableCell className="text-right">{item.percentage}%</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}

                {/* Materials */}
                {selectedDiary.materials?.length > 0 && (
                  <div>
                    <p className="text-sm font-medium mb-2">Materiali</p>
                    <Table>
                      <TableHeader><TableRow><TableHead>Tipo</TableHead><TableHead>Nome</TableHead><TableHead>RAL/Mesh</TableHead><TableHead className="text-right">Qty</TableHead></TableRow></TableHeader>
                      <TableBody>
                        {selectedDiary.materials.map((mat, i) => (
                          <TableRow key={i}><TableCell className="capitalize">{mat.type}</TableCell><TableCell>{mat.name}</TableCell><TableCell>{mat.ral || '-'}</TableCell><TableCell className="text-right">{mat.quantity}</TableCell></TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}

                {/* Notes */}
                {selectedDiary.operational_notes && (
                  <div>
                    <p className="text-sm font-medium mb-1">Note Operative</p>
                    <p className="text-sm text-slate-600 bg-slate-50 p-3 rounded">{selectedDiary.operational_notes}</p>
                  </div>
                )}

                {/* Attachments */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-sm font-medium">Allegati (Foto/PDF)</p>
                    <label className="cursor-pointer">
                      <input 
                        type="file" 
                        accept=".pdf,.jpg,.jpeg,.png,.gif" 
                        className="hidden" 
                        onChange={(e) => e.target.files?.[0] && handleUploadAttachment(e.target.files[0])}
                        disabled={uploading}
                      />
                      <Button variant="outline" size="sm" disabled={uploading} asChild>
                        <span><Upload size={14} className="mr-1" />{uploading ? 'Caricamento...' : 'Carica'}</span>
                      </Button>
                    </label>
                  </div>
                  {selectedDiary.attachments?.length === 0 ? (
                    <p className="text-xs text-slate-400 py-2">Nessun allegato. Puoi caricare foto o PDF della versione cartacea.</p>
                  ) : (
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                      {selectedDiary.attachments?.map(att => (
                        <div key={att.id} className="relative group border rounded p-2 bg-slate-50">
                          {att.original_name?.match(/\.(jpg|jpeg|png|gif)$/i) ? (
                            <a href={`${process.env.REACT_APP_BACKEND_URL}${att.file_url}`} target="_blank" rel="noopener noreferrer">
                              <img src={`${process.env.REACT_APP_BACKEND_URL}${att.file_url}`} alt={att.original_name} className="w-full h-20 object-cover rounded" />
                            </a>
                          ) : (
                            <a href={`${process.env.REACT_APP_BACKEND_URL}${att.file_url}`} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center h-20 bg-slate-100 rounded">
                              <FileText size={24} className="text-slate-400" />
                            </a>
                          )}
                          <p className="text-xs text-slate-600 mt-1 truncate">{att.original_name}</p>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="absolute top-1 right-1 p-1 h-auto opacity-0 group-hover:opacity-100 bg-white/80 text-red-600"
                            onClick={() => handleDeleteAttachment(att.id)}
                          >
                            <Trash2 size={12} />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Signature */}
                <div className="pt-4 border-t flex justify-between text-sm text-slate-500">
                  <span>Compilato da: {selectedDiary.compiled_by}</span>
                  <span>Data: {fmtDate(selectedDiary.signature_date || selectedDiary.date)}</span>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Dialog Export Diari Produzione */}
      <Dialog open={showExportMatrice} onOpenChange={setShowExportMatrice}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Esporta Diari di Produzione</DialogTitle>
            <DialogDescription>Seleziona cantiere e periodo per esportare tutti i dati in Excel.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-1.5">
              <Label>Cantiere</Label>
              <Select value={exportSiteId} onValueChange={setExportSiteId}>
                <SelectTrigger><SelectValue placeholder="Tutti i cantieri" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutti i cantieri</SelectItem>
                  {sites.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Data Inizio</Label>
                <Input type="date" value={exportDateFrom} onChange={(e) => setExportDateFrom(e.target.value)} data-testid="export-date-from" />
              </div>
              <div className="space-y-1.5">
                <Label>Data Fine</Label>
                <Input type="date" value={exportDateTo} onChange={(e) => setExportDateTo(e.target.value)} data-testid="export-date-to" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowExportMatrice(false)}>Annulla</Button>
            <Button onClick={handleExportMatrice} className="bg-green-600 hover:bg-green-700" data-testid="export-matrice-confirm"><Download size={14} className="mr-1" /> Scarica Excel</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Folder Manager Dialog */}
      <Dialog open={showFolderManager} onOpenChange={(open) => { if (!open) { setShowFolderManager(false); setEditingFolderId(null); setFolderForm({ name: '', pdl: '', description: '', color: '#1a4b8c' }); } }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Gestione Cartelle</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <Card className="border-slate-200">
              <CardContent className="p-4 space-y-3">
                <p className="text-sm font-semibold text-slate-700">{editingFolderId ? 'Modifica Cartella' : 'Nuova Cartella'}</p>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label>Nome *</Label>
                    <Input value={folderForm.name} onChange={e => setFolderForm(f => ({ ...f, name: e.target.value }))} placeholder="Es. PDL 348/C Sarpom" data-testid="folder-name" />
                  </div>
                  <div className="space-y-1.5">
                    <Label>PDL</Label>
                    <Input value={folderForm.pdl} onChange={e => setFolderForm(f => ({ ...f, pdl: e.target.value }))} placeholder="Es. 348/C" data-testid="folder-pdl" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label>Descrizione</Label>
                    <Input value={folderForm.description} onChange={e => setFolderForm(f => ({ ...f, description: e.target.value }))} placeholder="opzionale" />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Colore</Label>
                    <Input type="color" value={folderForm.color} onChange={e => setFolderForm(f => ({ ...f, color: e.target.value }))} className="h-9" />
                  </div>
                </div>
                <div className="flex gap-2 justify-end">
                  {editingFolderId && (
                    <Button variant="outline" onClick={() => { setEditingFolderId(null); setFolderForm({ name: '', pdl: '', description: '', color: '#1a4b8c' }); }}>Annulla</Button>
                  )}
                  <Button onClick={handleSaveFolder} data-testid="folder-save"><FolderPlus size={14} className="mr-1" /> {editingFolderId ? 'Aggiorna' : 'Crea Cartella'}</Button>
                </div>
              </CardContent>
            </Card>

            <div>
              <p className="text-sm font-semibold text-slate-700 mb-2">Cartelle Esistenti ({folders.length})</p>
              {folders.length === 0 ? (
                <p className="text-sm text-slate-500 text-center py-6">Nessuna cartella ancora creata</p>
              ) : (
                <div className="space-y-2">
                  {folders.map(f => (
                    <div key={f.id} className="flex items-center justify-between p-3 rounded border" style={{ borderColor: `${f.color}40`, background: `${f.color}08` }}>
                      <div className="flex items-center gap-2">
                        <Folder size={16} style={{ color: f.color }} />
                        <div>
                          <p className="text-sm font-medium">{f.name}</p>
                          <p className="text-xs text-slate-500">
                            {f.pdl ? `PDL ${f.pdl} - ` : ''}{f.diary_count || 0} diari
                            {f.description ? ` - ${f.description}` : ''}
                          </p>
                        </div>
                      </div>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="sm" onClick={() => { setEditingFolderId(f.id); setFolderForm({ name: f.name, pdl: f.pdl || '', description: f.description || '', color: f.color || '#1a4b8c' }); }}>
                          <Edit size={14} />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDeleteFolder(f.id)} data-testid={`folder-delete-${f.id}`}>
                          <Trash2 size={14} className="text-red-600" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowFolderManager(false)}>Chiudi</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Move-to-Folder Dialog */}
      <Dialog open={showMoveDialog} onOpenChange={setShowMoveDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Sposta in Cartella</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-slate-600">
              Diario del <strong>{fmtDate(selectedDiary?.date)}</strong> {selectedDiary?.site_name && `- ${selectedDiary.site_name}`}
            </p>
            <div className="space-y-1.5">
              <Label>Cartella di destinazione</Label>
              <Select value={moveTargetFolder} onValueChange={setMoveTargetFolder}>
                <SelectTrigger data-testid="move-target-folder"><SelectValue placeholder="Seleziona..." /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">Senza cartella</SelectItem>
                  {folders.map(f => <SelectItem key={f.id} value={f.id}>{f.name}{f.pdl ? ` - PDL ${f.pdl}` : ''}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowMoveDialog(false)}>Annulla</Button>
            <Button onClick={handleMoveToFolder} data-testid="move-confirm">Sposta</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
